/**
 * Normalized Bond Type and Features taxonomy for Advanced Search.
 * Codes are the API contract (`bondType` / `features` filters) and mirror the backend
 * `BondTypeCodes` / `FeatureCodes`; labels and groups are display-only.
 */

export type BondTypeCode =
  | "go_utgo"
  | "go_ltgo"
  | "go"
  | "certificates_of_obligation"
  | "certificates_of_participation"
  | "revenue"
  | "unclassified";

export type FeatureCode =
  | "special_assessment"
  | "tax_allocation_tif"
  | "securitized_tobacco_settlement"
  | "lease_appropriation"
  | "babs"
  | "tax_credit_program"
  | "notes_short_term"
  | "certificate_of_indebtedness";

export interface BondTypeOption {
  code: BondTypeCode;
  label: string;
  /** Label in filter pickers when it must differ from the display label. */
  filterLabel?: string;
}

export interface FeatureOption {
  code: FeatureCode;
  label: string;
}

export interface FeatureGroup {
  group: string;
  options: readonly FeatureOption[];
}

/** Display order of the Bond Type list. */
export const BOND_TYPE_OPTIONS: readonly BondTypeOption[] = [
  { code: "go_utgo", label: "Unlimited GO" },
  { code: "go_ltgo", label: "Limited GO" },
  { code: "go", label: "GO", filterLabel: "GO (Unspecified)" },
  { code: "certificates_of_obligation", label: "Certificates of Obligation" },
  { code: "certificates_of_participation", label: "Certificates of Participation" },
  { code: "revenue", label: "Revenue" },
  { code: "unclassified", label: "Unclassified" },
] as const;

/** Bond Type options for filter pickers, where GO with no tax pledge is labelled "Unspecified". */
export const BOND_TYPE_FILTER_OPTIONS: readonly BondTypeOption[] = BOND_TYPE_OPTIONS.map((o) => ({
  code: o.code,
  label: o.filterLabel ?? o.label,
}));

export const SPECIAL_PLEDGE_GROUP: FeatureGroup = {
  group: "Special Pledge",
  options: [
    { code: "special_assessment", label: "Special Assessment / Special Tax" },
    { code: "tax_allocation_tif", label: "Tax Allocation (TIF)" },
    { code: "securitized_tobacco_settlement", label: "Tobacco Settlement" },
    { code: "lease_appropriation", label: "Lease / Appropriation" },
  ],
};

export const INSTRUMENT_GROUP: FeatureGroup = {
  group: "Instrument",
  options: [
    { code: "babs", label: "Build America Bonds (BABs)" },
    { code: "tax_credit_program", label: "Tax Credit Bonds" },
    { code: "notes_short_term", label: "Short-Term Notes" },
    { code: "certificate_of_indebtedness", label: "Certificate of Indebtedness" },
  ],
};

export const FEATURE_GROUPS: readonly FeatureGroup[] = [SPECIAL_PLEDGE_GROUP, INSTRUMENT_GROUP];

/** Row code for the Double-Barreled control, which maps to the `isDoubleBarreled` flag rather than a Feature. */
export const DOUBLE_BARRELED_CODE = "double_barreled";
export const DOUBLE_BARRELED_LABEL = "Double-Barreled";

const BOND_TYPE_CODE_SET = new Set<string>(BOND_TYPE_OPTIONS.map((o) => o.code));
const FEATURE_CODE_SET = new Set<string>(FEATURE_GROUPS.flatMap((g) => g.options.map((o) => o.code)));

export function isBondTypeCode(value: string): value is BondTypeCode {
  return BOND_TYPE_CODE_SET.has(value);
}

export function isFeatureCode(value: string): value is FeatureCode {
  return FEATURE_CODE_SET.has(value);
}

const LABEL_BY_CODE: Record<string, string> = Object.fromEntries([
  ...BOND_TYPE_OPTIONS.map((o) => [o.code, o.label]),
  ...FEATURE_GROUPS.flatMap((g) => g.options.map((o) => [o.code, o.label])),
]);

/** Filter-picker label for a Bond Type code. */
export function bondTypeLabel(code: BondTypeCode): string {
  return BOND_TYPE_FILTER_OPTIONS.find((o) => o.code === code)?.label ?? code;
}

/** Label for a Bond Type code from an API payload; empty when the code is absent, the code itself when unknown. */
export function bondTypeLabelOrEmpty(code: string | null | undefined): string {
  if (!code) return "";
  return LABEL_BY_CODE[code] ?? code;
}

/**
 * Display labels for a security's Features, with Double-Barreled first when the flag is set. Unknown codes
 * show as the code itself so a taxonomy addition is visible before the frontend learns its label.
 */
export function featureLabels(
  features: readonly string[] | null | undefined,
  isDoubleBarreled?: boolean | null,
): string[] {
  const labels = (features ?? []).map((code) => LABEL_BY_CODE[code] ?? code);
  return isDoubleBarreled ? [DOUBLE_BARRELED_LABEL, ...labels] : labels;
}

export function featureLabel(code: FeatureCode): string {
  return LABEL_BY_CODE[code] ?? code;
}
